import React, { useState } from 'react';
import { FileText, X, Loader2, Copy, CheckCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { summarizeDocument } from '../lib/aiService';
import { useTranslation } from '../lib/i18n';

interface DocumentSummarizerProps {
  documentContent: string;
  isOpen: boolean;
  onClose: () => void;
}

const DocumentSummarizer: React.FC<DocumentSummarizerProps> = ({
  documentContent,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryLength, setSummaryLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [copied, setCopied] = useState(false);

  const generateSummary = async () => {
    if (!documentContent || documentContent.trim().length === 0) {
      setError(t('summarizer.noContent'));
      return;
    }

    setLoading(true);
    setError(null);
    setSummary('');

    try {
      const result = await summarizeDocument(documentContent, summaryLength);

      if (result.error) {
        setError(result.error);
      } else if (result.summary) {
        setSummary(result.summary);
      } else {
        setError(t('summarizer.noResult'));
      }
    } catch (err: any) {
      setError(err.message || t('summarizer.failedGenerate'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600 dark:text-blue-300" />
            </div>
            <h2 className="text-2xl font-bold">{t('summarizer.title')}</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium mb-3">{t('summarizer.length')}</label>
            <div className="flex gap-2">
              <Button
                variant={summaryLength === 'short' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSummaryLength('short')}
                disabled={loading}
              >
                {t('summarizer.brief')}
              </Button>
              <Button
                variant={summaryLength === 'medium' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSummaryLength('medium')}
                disabled={loading}
              >
                {t('summarizer.standard')}
              </Button>
              <Button
                variant={summaryLength === 'long' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSummaryLength('long')}
                disabled={loading}
              >
                {t('summarizer.detailed')}
              </Button>
            </div>
          </div>

          <Button
            onClick={generateSummary}
            disabled={loading || !documentContent}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('summarizer.generating')}
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                {t('summarizer.generate')}
              </>
            )}
          </Button>

          {error && (
            <div className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 p-4 rounded-lg">
              {error}
            </div>
          )}

          {summary && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">{t('summarizer.resultLabel')}</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                      {t('summarizer.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      {t('summarizer.copy')}
                    </>
                  )}
                </Button>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {summary}
                </p>
              </div>
            </div>
          )}

          {!summary && !loading && !error && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('summarizer.emptyDesc')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentSummarizer;
